import React from 'react';

export interface StampData {
  projectName?: string;
  sheetTitle?: string;
  docCode?: string;
  stage?: string;
  sheetNumber?: string | number;
  totalSheets?: string | number;
  company?: string;
  author?: string;
  checker?: string;
  date?: string;
}

interface GostStampProps {
  stampType?: 'gost_form1' | 'gost_form2a' | 'simple' | 'none';
  data: StampData;
  onDataChange?: (data: StampData) => void;
  isEditable?: boolean;
}

export const GostStamp: React.FC<GostStampProps> = ({
  stampType = 'gost_form1',
  data,
  onDataChange,
  isEditable = false,
}) => {
  if (stampType === 'none') return null;

  const handleChange = (field: keyof StampData, value: string) => {
    if (onDataChange) {
      onDataChange({ ...data, [field]: value });
    }
  };

  // Form 2a: subsequent sheets (185 x 15 mm)
  if (stampType === 'gost_form2a') {
    return (
      <div
        className="gost-stamp gost-form-2a absolute bottom-0 right-0 bg-white border-t-2 border-l-2 border-black text-black select-none pointer-events-auto"
        style={{
          width: '185mm',
          height: '15mm',
          boxSizing: 'border-box',
          fontSize: '9px',
          fontFamily: 'sans-serif',
          lineHeight: '1.2',
          WebkitFontSmoothing: 'antialiased',
          textRendering: 'geometricPrecision',
        }}
      >
        <div className="flex h-full">
          <div className="w-[110mm] border-r border-black p-1 flex flex-col justify-center">
            <div className="text-[7px] text-gray-500 uppercase">Обозначение документа</div>
            {isEditable ? (
              <input
                type="text"
                value={data.docCode || ''}
                placeholder="ЭОМ-01.01"
                onChange={(e) => handleChange('docCode', e.target.value)}
                className="w-full font-bold text-[10px] bg-blue-50 border-b border-blue-300 outline-none px-1"
              />
            ) : (
              <div className="font-bold text-[10px] truncate">{data.docCode || '—'}</div>
            )}
          </div>
          <div className="w-[25mm] border-r border-black p-1 flex flex-col items-center justify-center">
            <div className="text-[7px] text-gray-500 uppercase">Лист</div>
            {isEditable ? (
              <input
                type="text"
                value={data.sheetNumber || ''}
                placeholder="2"
                onChange={(e) => handleChange('sheetNumber', e.target.value)}
                className="w-full text-center font-bold text-[10px] bg-blue-50 border-b border-blue-300 outline-none px-1"
              />
            ) : (
              <div className="font-bold text-[10px]">{data.sheetNumber || '1'}</div>
            )}
          </div>
          <div className="flex-1 p-1 flex flex-col items-center justify-center">
            <div className="text-[7px] text-gray-500 uppercase">Листов</div>
            <div className="font-bold text-[10px]">{data.totalSheets || '1'}</div>
          </div>
        </div>
      </div>
    );
  }

  // Simple title block
  if (stampType === 'simple') {
    return (
      <div
        className="gost-stamp simple-stamp absolute bottom-0 right-0 bg-white border-t-2 border-l-2 border-black text-black select-none pointer-events-auto p-2"
        style={{
          width: '120mm',
          height: '25mm',
          boxSizing: 'border-box',
          fontSize: '10px',
          fontFamily: 'sans-serif',
          WebkitFontSmoothing: 'antialiased',
          textRendering: 'geometricPrecision',
        }}
      >
        <div className="font-bold text-[12px] truncate">{data.projectName || 'Проект'}</div>
        <div className="text-[11px] text-gray-700 truncate">{data.sheetTitle || 'Лист схемы'}</div>
        <div className="flex justify-between text-[9px] text-gray-500 mt-1 pt-1 border-t border-gray-300">
          <span>Лист {data.sheetNumber || 1} из {data.totalSheets || 1}</span>
          <span>{data.company || 'BAGSUS'}</span>
          <span>{data.date || new Date().toLocaleDateString('ru-RU')}</span>
        </div>
      </div>
    );
  }

  // ГОСТ 2.104-2006 Форма 1 (185 x 55 мм)
  return (
    <div
      className="gost-stamp gost-form-1 absolute bottom-0 right-0 bg-white border-t-2 border-l-2 border-black text-black select-none pointer-events-auto"
      style={{
        width: '185mm',
        height: '55mm',
        boxSizing: 'border-box',
        fontSize: '8px',
        fontFamily: 'sans-serif',
        lineHeight: '1.1',
        WebkitFontSmoothing: 'antialiased',
        textRendering: 'geometricPrecision',
      }}
    >
      <div className="flex h-full">
        {/* Левая колонка: разработчики / подписи (65 мм) */}
        <div className="w-[65mm] border-r-2 border-black flex flex-col">
          {/* Заголовки строк */}
          <div className="h-[7mm] border-b border-black flex items-center px-1 text-[7px] text-gray-500 font-semibold">
            <div className="w-[17mm]">Изм. Кол.уч</div>
            <div className="w-[23mm]">Лист №док.</div>
            <div className="w-[13mm]">Подп.</div>
            <div className="w-[12mm]">Дата</div>
          </div>
          <div className="h-[7mm] border-b border-black flex items-center px-1">
            <div className="w-[17mm] text-[7px] text-gray-600">Разраб.</div>
            <div className="w-[23mm] font-semibold text-[8px] truncate">{data.author || 'Инженер'}</div>
            <div className="w-[13mm] border-b border-gray-300 h-3"></div>
            <div className="w-[12mm] text-[7px] text-center">{data.date || ''}</div>
          </div>
          <div className="h-[7mm] border-b border-black flex items-center px-1">
            <div className="w-[17mm] text-[7px] text-gray-600">Пров.</div>
            <div className="w-[23mm] font-semibold text-[8px] truncate">{data.checker || 'ГИП'}</div>
            <div className="w-[13mm] border-b border-gray-300 h-3"></div>
            <div className="w-[12mm] text-[7px] text-center">{data.date || ''}</div>
          </div>
          <div className="h-[7mm] border-b border-black flex items-center px-1">
            <div className="w-[17mm] text-[7px] text-gray-600">Т.контр.</div>
            <div className="w-[23mm]"></div>
            <div className="w-[13mm]"></div>
            <div className="w-[12mm]"></div>
          </div>
          <div className="h-[14mm] border-b border-black flex flex-col justify-center px-1">
            <div className="text-[7px] text-gray-600">Нормоконтр.</div>
          </div>
          <div className="flex-1 flex items-center px-1">
            <div className="w-[17mm] text-[7px] text-gray-600">Утв.</div>
            <div className="w-[23mm]"></div>
            <div className="w-[13mm]"></div>
            <div className="w-[12mm]"></div>
          </div>
        </div>

        {/* Правая часть (120 мм) */}
        <div className="flex-1 flex flex-col">
          {/* Шифр документа (верхняя строка, 15 мм) */}
          <div className="h-[15mm] border-b-2 border-black flex items-center justify-center px-2 bg-gray-50">
            <span className="font-bold text-[12px] tracking-wider text-center truncate">
              {data.docCode || 'ЭОМ-01'}
            </span>
          </div>

          {/* Средняя часть: Название проекта и листа (25 мм) */}
          <div className="h-[25mm] border-b-2 border-black flex">
            {/* Название проекта и листа (70 мм) */}
            <div className="w-[70mm] border-r-2 border-black p-1 flex flex-col justify-between">
              <div className="text-[9px] font-bold text-gray-800 line-clamp-2" title={data.projectName}>
                {data.projectName || 'Объект'}
              </div>
              <div className="text-[10px] font-extrabold text-blue-900 line-clamp-2" title={data.sheetTitle}>
                {data.sheetTitle || 'Наименование листа'}
              </div>
            </div>

            {/* Стадия, Лист, Листов (50 мм) */}
            <div className="flex-1 flex flex-col">
              <div className="h-[10mm] border-b border-black flex text-center text-[7px]">
                <div className="w-[15mm] border-r border-black flex flex-col justify-center">
                  <span className="text-gray-500">Стадия</span>
                  <span className="font-bold text-[9px]">{data.stage || 'Р'}</span>
                </div>
                <div className="w-[15mm] border-r border-black flex flex-col justify-center">
                  <span className="text-gray-500">Лист</span>
                  <span className="font-extrabold text-[10px]">{data.sheetNumber || '1'}</span>
                </div>
                <div className="flex-1 flex flex-col justify-center">
                  <span className="text-gray-500">Листов</span>
                  <span className="font-bold text-[9px]">{data.totalSheets || '1'}</span>
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center p-1 text-center font-bold text-[9px] text-gray-700">
                {data.company || 'BAGSUS'}
              </div>
            </div>
          </div>

          {/* Нижняя полоса: организация (15 мм) */}
          <div className="flex-1 flex items-center justify-between px-2 bg-gray-50 text-[8px] text-gray-600">
            <span>Система схем e2.bagsus.ru</span>
            <span>Формат листа</span>
          </div>
        </div>
      </div>
    </div>
  );
};
